import { ApiResponse } from '@shared/types';
import {
  FunctionsFetchError,
  FunctionsHttpError,
  FunctionsRelayError,
} from '@supabase/supabase-js';
import { OpenNewDayRequest, OpenNewDayResult } from '../types/businessDay';
import { supabase } from './supabase';

export const openNewDay = async (
  request: OpenNewDayRequest,
): Promise<ApiResponse<OpenNewDayResult>> => {
  try {
    const { data, error } = await supabase.functions.invoke('open-new-day', {
      body: {
        user_id: request.userId,
        outlet_id: request.outletId,
        target_date: request.targetDate,
      },
    });

    if (error) {
      if (error instanceof FunctionsHttpError) {
        try {
          const payload = await error.context.json();
          return {
            success: false,
            error:
              payload?.error ||
              payload?.message ||
              error.message ||
              'Failed to open a new business day',
            data: undefined,
          };
        } catch {
          return {
            success: false,
            error: error.message || 'Failed to open a new business day',
            data: undefined,
          };
        }
      }

      if (error instanceof FunctionsFetchError) {
        // FunctionsFetchError.message is hard-coded by @supabase/functions-js
        // to "Failed to send a request to the Edge Function" — always
        // truthy, so `error.message || fallback` never reaches the
        // fallback. This normally shouldn't be hit at all now that the
        // caller (openNewDayForCurrentOutlet) checks isOnline() first, but
        // covers the race where connectivity drops between that check and
        // this call.
        return {
          success: false,
          error:
            'You are offline — connect to the internet to open a new business day.',
          data: undefined,
        };
      }

      if (error instanceof FunctionsRelayError) {
        return {
          success: false,
          error:
            'Could not reach the new-day service right now. Please try again shortly.',
          data: undefined,
        };
      }

      return {
        success: false,
        error: error.message || 'Failed to open a new business day',
        data: undefined,
      };
    }

    return {
      success: true,
      data: data as OpenNewDayResult,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to open a new business day',
      data: undefined,
    };
  }
};
